import { useEffect, useMemo, useState } from 'react';
import { eyebrowStyle, ghostLinkStyle, pageStyle } from '@kilat/ui';
import { PAGE_FRAME_MAX, dashboardPageStyle, sectionGridStyle } from '../lib/layout.js';
import { Link } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';

import { useAuth } from '../lib/use-auth.js';
import { logger } from '../lib/logger.js';
import { useI18n } from '../lib/use-i18n.js';
import { useActiveOutlet } from '../lib/use-active-outlet.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { PeerBenchmarkCard } from '../components/peer-benchmark-card.js';
import { RegularsCard } from '../components/regulars-card.js';
import { ReportExportButton } from '../components/report-export-button.js';
import { AttributionSourcesCard, type SourceRow } from '../components/attribution-sources-card.js';
import { tenantsRepo } from '../lib/repositories/tenants-repo.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { ticketsHistoryRepoFor, statsRepoFor } from '../lib/repositories/outlet-repos.js';
import {
  money,
  rollupTicketsToStats,
  type AttributionSource,
  type DailyStats,
  type DailyStatsRollup,
  type PaymentMethod,
  type Ticket,
} from '@kilat/shared';

/**
 * /reports — operator stats.
 *
 * COST (Sprint 122 — audit 2026-07-05 §2 P1, cost hotspot #2): the O(1) rollup
 * is now live. `aggregationsDaily` folds each outlet's previous-day
 * `ticketsHistory` into one `stats/{YYYY-MM-DD}` doc. This page reads:
 *   - the daily `stats` docs for the completed past days in the selected range
 *     (≤ ~30 reads — one per day, missing day = zero day), AND
 *   - a bounded live `ticketsHistory` listener scoped to TODAY only, reduced
 *     in-memory via the same `rollupTicketsToStats` helper the cron uses, so
 *     today's in-progress numbers appear before the nightly job runs.
 * The two are merged into per-day rollups. Historical days before this shipped
 * (no stats doc) simply read as zero — graceful, no crash, no dead-end.
 *
 * All day boundaries use SGT (UTC+8, the platform clock — same as the rollover
 * job) so today's live slice stitches to yesterday's stats doc at one seam.
 *
 * Per [feedback-no-dead-ends]: every state has explicit copy:
 *   - signed-out, no-tenant, no-outlet → splash with next-action
 *   - loading → skeleton KPI cards
 *   - empty (no tickets in range) → friendly empty state explaining why
 *   - error → red card with the failure message
 *   - populated → real numbers
 */

type Range = 'today' | 'week' | 'month' | 'last30';

/** Hard backstop on docs today's live listener holds — one carwash-day never nears this. */
const REPORTS_MAX_DOCS = 20_000;
/** Backstop on daily stats docs a range query holds (last30 needs ≤ 30). */
const REPORTS_MAX_STATS_DOCS = 40;

const SGT_OFFSET_MS = 8 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

const RANGE_KEYS: readonly Range[] = ['today', 'week', 'month', 'last30'];

export function ReportsPage() {
  const { user, claims, loading: authLoading } = useAuth();
  const { t } = useI18n();
  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;
  const { outletId, loading: outletLoading } = useActiveOutlet();
  const [range, setRange] = useState<Range>('today');
  // Bump to force both snapshot listeners to re-subscribe (retry after an error).
  const [reloadTick, setReloadTick] = useState(0);

  // Range → { today's SGT-midnight, today's day key, completed past-day keys }.
  // Recomputed only when the range chip changes (captures "now" at that point).
  const win = useMemo(() => computeWindow(range), [range]);

  // Past-day stats docs (one read/day; skipped entirely for the 'today' range).
  const statsSubscribe = useMemo(() => {
    if (!tenantId || !outletId || win.pastKeys.length === 0) return null;
    const repo = statsRepoFor(tenantId, outletId);
    const first = win.pastKeys[0];
    const last = win.pastKeys[win.pastKeys.length - 1];
    return (cb: (vs: DailyStats[]) => void) =>
      repo.listSnapshotBounded(
        {
          where: [
            ['day', '>=', first],
            ['day', '<=', last],
          ],
          orderBy: [['day', 'asc']],
          limitTo: REPORTS_MAX_STATS_DOCS,
        },
        cb,
      );
  }, [tenantId, outletId, win]);

  // Today's in-progress tickets — bounded to a single SGT day, reduced live.
  const todaySubscribe = useMemo(() => {
    if (!tenantId || !outletId) return null;
    const repo = ticketsHistoryRepoFor(tenantId, outletId);
    const since = Timestamp.fromMillis(win.todayStartMs);
    return (cb: (vs: Ticket[]) => void) =>
      repo.listSnapshotBounded(
        {
          where: [['payment.receivedAt', '>=', since]],
          orderBy: [['payment.receivedAt', 'desc']],
          limitTo: REPORTS_MAX_DOCS,
        },
        cb,
      );
  }, [tenantId, outletId, win]);

  const statsState = useTrackedSnapshot<DailyStats[]>(
    ['reports-stats', tenantId, outletId, range, reloadTick],
    statsSubscribe,
  );
  const todayState = useTrackedSnapshot<Ticket[]>(
    ['reports-today', tenantId, outletId, reloadTick],
    todaySubscribe,
  );

  // When there are no past days (the 'today' range), the stats listener is null
  // and its state never resolves — treat that as "loaded, empty" not "loading".
  const hasPast = win.pastKeys.length > 0;
  const statsLoading = hasPast ? statsState.loading : false;
  const pastDocs: DailyStats[] = hasPast ? (statsState.data ?? []) : [];
  const loading = statsLoading || todayState.loading;
  const error = statsState.error ?? todayState.error;

  if (authLoading) return <Splash label={t('dashboard.reports.loadingReports')} />;
  if (!user) {
    return (
      <Splash
        label={t('dashboard.states.reportsSignIn')}
        action={
          <Link to="/queue" style={splashLinkStyle}>
            {t('dashboard.states.backToQueue')}
          </Link>
        }
      />
    );
  }
  if (!tenantId) return <Splash label={t('dashboard.states.loadingAccount')} />;
  if (outletLoading) return <Splash label={t('dashboard.reports.loadingOutlet')} />;
  if (!outletId) {
    return (
      <Splash
        label={t('dashboard.states.reportsNoOutlet')}
        action={
          <Link to="/queue" style={splashLinkStyle}>
            {t('dashboard.states.backToQueue')}
          </Link>
        }
      />
    );
  }
  if (error) {
    return (
      <Splash
        label={t('dashboard.states.reportsError')}
        action={
          <div
            style={{
              display: 'flex',
              gap: 'var(--space-2)',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              onClick={() => setReloadTick((n) => n + 1)}
              style={splashPrimaryButtonStyle}
            >
              {t('common.tryAgain')}
            </button>
            <Link to="/queue" style={splashLinkStyle}>
              {t('dashboard.states.backToQueue')}
            </Link>
          </div>
        }
      />
    );
  }

  return (
    <main style={dashboardPageStyle}>
      <Header range={range} setRange={setRange} />
      <section
        style={{
          maxWidth: PAGE_FRAME_MAX,
          margin: '0 auto',
          padding: '0 var(--space-5) var(--space-12) var(--space-5)',
        }}
      >
        {loading ? (
          <SkeletonReports />
        ) : (
          <ReportsBody
            pastDocs={pastDocs}
            todayTickets={todayState.data ?? []}
            todayStartMs={win.todayStartMs}
            todayKey={win.todayKey}
            range={range}
          />
        )}
      </section>
    </main>
  );
}

// ── header / shell ─────────────────────────────────────────────────────

function Header({
  range,
  setRange,
}: {
  readonly range: Range;
  readonly setRange: (r: Range) => void;
}) {
  const { t } = useI18n();
  return (
    <header
      style={{
        background: 'var(--color-surface)',
        borderBottom: '1px solid var(--color-border)',
        marginBottom: 'var(--space-6)',
      }}
    >
      <div
        style={{
          maxWidth: PAGE_FRAME_MAX,
          margin: '0 auto',
          padding: 'var(--space-5)',
          display: 'flex',
          gap: 'var(--space-4)',
          alignItems: 'center',
        }}
      >
        <div>
          <p style={eyebrowStyle}>{t('dashboard.reports.eyebrow')}</p>
          <h1
            style={{
              margin: 'var(--space-1) 0 0 0',
              fontSize: 'var(--text-h1)',
              fontWeight: 600,
              lineHeight: 1.2,
            }}
          >
            {t('dashboard.reports.title')}
          </h1>
        </div>
        <div
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
          }}
        >
          <Link to="/queue" style={ghostLinkStyle}>
            {t('dashboard.states.backToQueue')}
          </Link>
        </div>
      </div>
      <nav
        style={{
          maxWidth: PAGE_FRAME_MAX,
          margin: '0 auto',
          padding: '0 var(--space-5) var(--space-3) var(--space-5)',
          display: 'flex',
          gap: 'var(--space-2)',
          overflowX: 'auto',
        }}
      >
        {RANGE_KEYS.map((r) => {
          const active = r === range;
          return (
            <button
              key={r}
              type="button"
              onClick={() => setRange(r)}
              style={{
                padding: 'var(--space-2) var(--space-4)',
                borderRadius: 'var(--radius-pill)',
                border: '1px solid var(--color-border)',
                background: active ? 'var(--color-primary)' : 'transparent',
                color: active ? 'var(--color-primary-fg)' : 'var(--color-on-surface)',
                fontSize: 'var(--text-meta)',
                fontWeight: 500,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {t(`dashboard.reports.ranges.${r}`)}
            </button>
          );
        })}
      </nav>
    </header>
  );
}

// ── body ────────────────────────────────────────────────────────────────

function ReportsBody({
  pastDocs,
  todayTickets,
  todayStartMs,
  todayKey,
  range,
}: {
  readonly pastDocs: readonly DailyStats[];
  readonly todayTickets: readonly Ticket[];
  readonly todayStartMs: number;
  readonly todayKey: string;
  readonly range: Range;
}) {
  const { t } = useI18n();
  const { claims } = useAuth();
  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;
  const brand = usePlatformBrand();
  // The cohort an operator is measured against is decided by how many outlets
  // they run, so a one-bay shop is never compared with a chain.
  const { outlets, outletId } = useActiveOutlet();

  /**
   * Peer benchmarking is OPT-IN and starts off.
   *
   * Defaulting to false is the whole point: an operator's figures must not join
   * an aggregate because they never found a setting. Until they turn it on, the
   * card shows the invitation and explains the deal — you see the comparison
   * because you are part of it.
   *
   * Read from the tenant doc rather than held locally, because the same flag is
   * what the publisher filters on. A local toggle would show a comparison built
   * from a cohort this tenant never joined.
   */
  const [benchmarksOptIn, setBenchmarksOptIn] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    tenantsRepo
      .get(tenantId)
      .then((doc) => {
        if (!cancelled) setBenchmarksOptIn(doc?.profile.benchmarksOptIn === true);
      })
      .catch((e: unknown) => {
        // A failed read must not silently opt someone IN.
        logger.error('benchmark opt-in read failed', { error: String(e) });
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  async function enableBenchmarks(): Promise<void> {
    if (!tenantId) return;
    try {
      await tenantsRepo.update(tenantId, { profile: { benchmarksOptIn: true } });
      setBenchmarksOptIn(true);
    } catch (e: unknown) {
      logger.error('benchmark opt-in failed', { error: String(e) });
    }
  }

  // Build one rollup per day: past days from their stats docs, today from the
  // live ticket listener reduced with the SAME helper the cron uses.
  const exportWindow = useMemo(() => computeWindow(range), [range]);
  const exportFrom = exportWindow.pastKeys[0] ?? exportWindow.todayKey;
  const exportTo = exportWindow.todayKey;

  const days = useMemo<DayView[]>(() => {
    const list: DayView[] = pastDocs.map((d) => ({
      dateMs: dayKeyToMs(d.day),
      dayKey: d.day,
      rollup: {
        ticketCount: d.ticketCount,
        grossRevenueSen: d.grossRevenueSen,
        byMethod: d.byMethod,
        byPackage: d.byPackage,
        bySource: d.bySource,
      },
    }));
    list.push({
      dateMs: todayStartMs,
      dayKey: todayKey,
      rollup: rollupTicketsToStats(todayTickets),
    });
    return list;
  }, [pastDocs, todayTickets, todayStartMs, todayKey]);

  const agg = useMemo(() => aggregateDays(days, range), [days, range]);

  if (agg.count === 0) {
    return (
      <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
        <EmptyCard
          title={t('dashboard.reports.emptyRangeTitle', {
            range: t(`dashboard.reports.ranges.${range}`).toLowerCase(),
          })}
          body={t('dashboard.reports.emptyRangeBody')}
        />
        {/* The regulars card is a LIFETIME figure, so it belongs here too.
            It used to sit only in the populated branch, which meant an
            operator who opened /reports before the day's first wash was told
            "no tickets today" and shown nothing else — the emptiest possible
            reading of a business that has 40 washes and 7 regulars behind it.
            What you have built does not disappear because today is quiet. */}
        {tenantId ? <RegularsCard tenantId={tenantId} /> : null}
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <KpiRow
        revenue={agg.revenue}
        tickets={agg.count}
        avgTicket={agg.count > 0 ? Math.round(agg.revenue / agg.count) : 0}
      />
      {tenantId && outletId ? (
        <ReportExportButton
          tenantId={tenantId}
          outletId={outletId}
          from={exportFrom}
          to={exportTo}
        />
      ) : null}
      {/* Paired cards share a row on a wide screen; the trend chart keeps the
          full width because its bars are the point. */}
      <div style={sectionGridStyle}>
        {tenantId ? <RegularsCard tenantId={tenantId} /> : null}
        <PeerBenchmarkCard
          platformBrandId={brand.id}
          ownAvgTicketSen={agg.count > 0 ? Math.round(agg.revenue / agg.count) : 0}
          outletCount={outlets.length}
          optedIn={benchmarksOptIn}
          onOptIn={() => void enableBenchmarks()}
        />
      </div>
      {range !== 'today' ? <DailyBars data={agg.byDay} /> : null}
      <AttributionSourcesCard rows={agg.bySource} />
      <div style={sectionGridStyle}>
        <Section
          title={t('dashboard.reports.topPackagesTitle')}
          subtitle={t('dashboard.reports.topPackagesSubtitle')}
        >
          <BreakdownList
            rows={agg.byPackage.map((r) => ({
              label: r.packageName,
              sublabel: t(
                r.count === 1 ? 'dashboard.reports.ticketsOne' : 'dashboard.reports.ticketsOther',
                {
                  count: r.count,
                },
              ),
              value: r.revenue,
              max: agg.byPackage[0]?.revenue ?? 1,
            }))}
          />
        </Section>
        <Section
          title={t('dashboard.reports.paymentMethodsTitle')}
          subtitle={t('dashboard.reports.paymentMethodsSubtitle')}
        >
          <BreakdownList
            rows={agg.byMethod.map((r) => ({
              label: t(`dashboard.pos.paymentMethods.${r.method}`),
              sublabel: t(
                r.count === 1 ? 'dashboard.reports.ticketsOne' : 'dashboard.reports.ticketsOther',
                {
                  count: r.count,
                },
              ),
              value: r.revenue,
              max: agg.byMethod[0]?.revenue ?? 1,
            }))}
          />
        </Section>
      </div>
    </div>
  );
}

// ── KPI cards ──────────────────────────────────────────────────────────

function KpiRow({
  revenue,
  tickets,
  avgTicket,
}: {
  readonly revenue: number;
  readonly tickets: number;
  readonly avgTicket: number;
}) {
  const { t } = useI18n();
  return (
    <div
      style={{
        display: 'grid',
        gap: 'var(--space-3)',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))',
      }}
    >
      <KpiCard label={t('dashboard.reports.kpiRevenue')} value={money.format(revenue)} primary />
      <KpiCard label={t('dashboard.reports.kpiTickets')} value={String(tickets)} />
      <KpiCard label={t('dashboard.reports.kpiAvgTicket')} value={money.format(avgTicket)} />
    </div>
  );
}

function KpiCard({
  label,
  value,
  primary = false,
}: {
  readonly label: string;
  readonly value: string;
  readonly primary?: boolean;
}) {
  return (
    <article
      style={{
        padding: 'var(--space-5)',
        borderRadius: 'var(--radius-lg)',
        background: primary ? 'var(--color-primary)' : 'var(--color-surface)',
        color: primary ? 'var(--color-primary-fg)' : 'var(--color-on-surface)',
        border: primary ? 'none' : '1px solid var(--color-border)',
        boxShadow: primary ? 'var(--shadow-2)' : 'var(--shadow-1)',
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          letterSpacing: 2,
          textTransform: 'uppercase',
          opacity: primary ? 0.85 : 1,
          color: primary ? 'inherit' : 'var(--color-on-surface-muted)',
          fontWeight: 600,
        }}
      >
        {label}
      </p>
      <p
        style={{
          margin: 'var(--space-2) 0 0 0',
          fontSize: 'var(--text-h1)',
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.01em',
          lineHeight: 1.1,
        }}
      >
        {value}
      </p>
    </article>
  );
}

// ── daily bars ─────────────────────────────────────────────────────────

interface DayBucket {
  readonly dateMs: number;
  readonly label: string;
  readonly revenue: number;
  readonly count: number;
}

function DailyBars({ data }: { readonly data: readonly DayBucket[] }) {
  const { t } = useI18n();
  const maxRevenue = Math.max(1, ...data.map((d) => d.revenue));
  return (
    <Section
      title={t('dashboard.reports.dailyRevenueTitle')}
      subtitle={t('dashboard.reports.dailyRevenueSubtitle')}
    >
      <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
        {data.map((d) => {
          const widthPct = Math.max(2, (d.revenue / maxRevenue) * 100);
          return (
            <div
              key={d.dateMs}
              style={{
                display: 'grid',
                gridTemplateColumns: '90px 1fr 100px',
                gap: 'var(--space-3)',
                alignItems: 'center',
              }}
            >
              <span
                style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}
              >
                {d.label}
              </span>
              <div
                style={{
                  background: 'var(--tint-subtle)',
                  borderRadius: 'var(--radius-sm)',
                  height: 24,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${widthPct}%`,
                    height: '100%',
                    background: 'var(--color-primary)',
                    borderRadius: 'var(--radius-sm)',
                    transition: 'width var(--duration-2) var(--ease-snappy)',
                  }}
                />
              </div>
              <span
                style={{
                  fontSize: 'var(--text-meta)',
                  fontVariantNumeric: 'tabular-nums',
                  textAlign: 'right',
                  fontWeight: 500,
                }}
              >
                {money.format(d.revenue)}
              </span>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

// ── breakdown list (used by packages + methods) ────────────────────────

interface BreakdownRow {
  readonly label: string;
  readonly sublabel: string;
  readonly value: number;
  readonly max: number;
}

function BreakdownList({ rows }: { readonly rows: readonly BreakdownRow[] }) {
  const { t } = useI18n();
  if (rows.length === 0) {
    return (
      <p
        style={{ margin: 0, fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}
      >
        {t('dashboard.reports.emptyBreakdown')}
      </p>
    );
  }
  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      {rows.map((r) => {
        const widthPct = Math.max(2, (r.value / r.max) * 100);
        return (
          <div key={r.label}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: 4,
              }}
            >
              <div>
                <span style={{ fontSize: 'var(--text-body)', fontWeight: 500 }}>{r.label}</span>
                <span
                  style={{
                    marginLeft: 'var(--space-2)',
                    fontSize: 'var(--text-meta)',
                    color: 'var(--color-on-surface-muted)',
                  }}
                >
                  {r.sublabel}
                </span>
              </div>
              <span
                style={{
                  fontSize: 'var(--text-meta)',
                  fontWeight: 500,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {money.format(r.value)}
              </span>
            </div>
            <div
              style={{
                background: 'var(--tint-subtle)',
                borderRadius: 'var(--radius-sm)',
                height: 8,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${widthPct}%`,
                  height: '100%',
                  background: 'var(--color-primary)',
                  borderRadius: 'var(--radius-sm)',
                  transition: 'width var(--duration-2) var(--ease-snappy)',
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── shared layout ──────────────────────────────────────────────────────

function Section({
  title,
  subtitle,
  children,
}: {
  readonly title: string;
  readonly subtitle?: string;
  readonly children: React.ReactNode;
}) {
  return (
    <section
      style={{
        padding: 'var(--space-5)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        boxShadow: 'var(--shadow-1)',
      }}
    >
      <header style={{ marginBottom: 'var(--space-4)' }}>
        <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>{title}</h2>
        {subtitle ? (
          <p
            style={{
              margin: 'var(--space-1) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {subtitle}
          </p>
        ) : null}
      </header>
      {children}
    </section>
  );
}

function SkeletonReports() {
  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))',
          gap: 'var(--space-3)',
        }}
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              height: 110,
              borderRadius: 'var(--radius-lg)',
              background: 'var(--color-border)',
            }}
          />
        ))}
      </div>
      {[0, 1].map((i) => (
        <div
          key={i}
          style={{
            height: 200,
            borderRadius: 'var(--radius-lg)',
            background: 'var(--color-border)',
          }}
        />
      ))}
    </div>
  );
}

function EmptyCard({ title, body }: { readonly title: string; readonly body: string }) {
  return (
    <article
      style={{
        padding: 'var(--space-8) var(--space-5)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        textAlign: 'center',
      }}
    >
      <h2 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>{title}</h2>
      <p
        style={{
          margin: 'var(--space-3) auto 0 auto',
          maxWidth: 420,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          lineHeight: 1.5,
        }}
      >
        {body}
      </p>
    </article>
  );
}

function Splash({ label, action }: { readonly label: string; readonly action?: React.ReactNode }) {
  return (
    <main style={pageStyle}>
      <section
        style={{
          maxWidth: 480,
          margin: 'var(--space-8) auto',
          padding: 'var(--space-6)',
          borderRadius: 'var(--radius-lg)',
          background: 'var(--color-surface)',
          textAlign: 'center',
          display: 'grid',
          gap: 'var(--space-4)',
          justifyItems: 'center',
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {label}
        </p>
        {action}
      </section>
    </main>
  );
}

const splashPrimaryButtonStyle: React.CSSProperties = {
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  padding: '10px 16px',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-meta)',
  fontWeight: 500,
  cursor: 'pointer',
};

const splashLinkStyle: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  textDecoration: 'none',
  padding: '10px 16px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-border-strong)',
  display: 'inline-flex',
  alignItems: 'center',
};

// ── data math ──────────────────────────────────────────────────────────

/** One day's numbers, whatever the source (past stats doc or live today). */
interface DayView {
  readonly dateMs: number;
  readonly dayKey: string;
  readonly rollup: DailyStatsRollup;
}

interface PackageBucket {
  readonly packageId: string;
  readonly packageName: string;
  readonly revenue: number;
  readonly count: number;
}

interface MethodBucket {
  readonly method: PaymentMethod;
  readonly revenue: number;
  readonly count: number;
}

interface ReportAggregate {
  readonly revenue: number;
  readonly count: number;
  readonly byDay: DayBucket[];
  readonly byPackage: PackageBucket[];
  readonly byMethod: MethodBucket[];
  /** Share & grow: paid washes per attribution source, most first. */
  readonly bySource: SourceRow[];
}

/** SGT `YYYY-MM-DD` for a UTC ms instant. */
function sgtDayKey(ms: number): string {
  const d = new Date(ms + SGT_OFFSET_MS);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(
    d.getUTCDate(),
  ).padStart(2, '0')}`;
}

/** UTC ms of SGT-midnight for the SGT day containing `ms`. */
function sgtDayStartMs(ms: number): number {
  const d = new Date(ms + SGT_OFFSET_MS);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) - SGT_OFFSET_MS;
}

/** UTC ms of SGT-midnight for a `YYYY-MM-DD` key. */
function dayKeyToMs(key: string): number {
  const [y, m, d] = key.split('-').map(Number);
  return Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1) - SGT_OFFSET_MS;
}

interface ReportWindow {
  readonly todayStartMs: number;
  readonly todayKey: string;
  /** Completed SGT days in the range, oldest → newest (excludes today). */
  readonly pastKeys: string[];
}

/**
 * Resolve a range chip into today's SGT-midnight + the completed past-day keys.
 * Boundaries mirror the pre-rollup in-memory filter (Monday-week, 1st-of-month,
 * rolling 30) but on the SGT clock so the seam with the nightly job is exact.
 */
function computeWindow(range: Range): ReportWindow {
  const now = Date.now();
  const todayStartMs = sgtDayStartMs(now);
  const todayKey = sgtDayKey(todayStartMs);

  let rangeStartMs: number;
  if (range === 'today') {
    rangeStartMs = todayStartMs;
  } else if (range === 'week') {
    // Monday-start week (Sunday treated as the last day; common SEA convention).
    const dow = new Date(todayStartMs + SGT_OFFSET_MS).getUTCDay(); // 0=Sun … 6=Sat
    const offset = dow === 0 ? 6 : dow - 1;
    rangeStartMs = todayStartMs - offset * DAY_MS;
  } else if (range === 'month') {
    const d = new Date(todayStartMs + SGT_OFFSET_MS);
    rangeStartMs = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1) - SGT_OFFSET_MS;
  } else {
    // Last 30 days inclusive of today → 29 completed past days + today.
    rangeStartMs = todayStartMs - 29 * DAY_MS;
  }

  const pastKeys: string[] = [];
  for (let ms = rangeStartMs; ms < todayStartMs; ms += DAY_MS) {
    pastKeys.push(sgtDayKey(ms));
  }
  return { todayStartMs, todayKey, pastKeys };
}

/** Merge per-day rollups into the KPIs + bars + breakdowns the UI renders. */
function aggregateDays(days: readonly DayView[], range: Range): ReportAggregate {
  let revenue = 0;
  let count = 0;
  const bars: DayBucket[] = [];
  const pkgMap = new Map<string, PackageBucket>();
  const methodMap = new Map<PaymentMethod, MethodBucket>();
  const sourceMap = new Map<AttributionSource, number>();

  for (const dv of days) {
    for (const s of dv.rollup.bySource) {
      sourceMap.set(s.source, (sourceMap.get(s.source) ?? 0) + s.count);
    }
    revenue += dv.rollup.grossRevenueSen;
    count += dv.rollup.ticketCount;

    // A day only becomes a bar if it saw tickets (matches the old behaviour of
    // omitting empty days from the daily-revenue chart).
    if (dv.rollup.ticketCount > 0) {
      bars.push({
        dateMs: dv.dateMs,
        label: formatDayLabel(new Date(dv.dateMs), range),
        revenue: dv.rollup.grossRevenueSen,
        count: dv.rollup.ticketCount,
      });
    }

    for (const m of dv.rollup.byMethod) {
      const existing = methodMap.get(m.method);
      methodMap.set(m.method, {
        method: m.method,
        revenue: (existing?.revenue ?? 0) + m.revenueSen,
        count: (existing?.count ?? 0) + m.count,
      });
    }
    for (const p of dv.rollup.byPackage) {
      const existing = pkgMap.get(p.packageId);
      pkgMap.set(p.packageId, {
        packageId: p.packageId,
        packageName: existing?.packageName ?? p.packageName,
        revenue: (existing?.revenue ?? 0) + p.revenueSen,
        count: (existing?.count ?? 0) + p.count,
      });
    }
  }

  bars.sort((a, b) => a.dateMs - b.dateMs);
  const byPackage = Array.from(pkgMap.values()).sort((a, b) => b.revenue - a.revenue);
  const byMethod = Array.from(methodMap.values()).sort((a, b) => b.revenue - a.revenue);
  const bySource = Array.from(sourceMap, ([source, n]) => ({ source, count: n })).sort(
    (a, b) => b.count - a.count,
  );
  return { revenue, count, byDay: bars, byPackage, byMethod, bySource };
}

function formatDayLabel(d: Date, range: Range): string {
  if (range === 'week') {
    return d.toLocaleDateString(undefined, { weekday: 'short' });
  }
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

// ── styles ─────────────────────────────────────────────────────────────
